"use client";
import React, { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useAppSelector, useAppDispatch } from './lib/hooks';
import { getTourCategories } from './lib/reducers/tourCategoriesReducer';
import { getFeaturedCities } from './lib/reducers/citiesReducer';
import { getSocialNetworks } from './lib/reducers/settingsReducer';
import { getRandomReviews } from './lib/reducers/reviewsReducer';

// Routes that don't actually use the tour-categories / featured-cities /
// random-reviews payloads on first paint — skip the ~150 KB of fetches so
// auth-style pages don't pay the cost.
const NON_CONTENT_PATH_PATTERNS = [
  /\/login(\/|$)/,
  /\/signup(\/|$)/,
  /\/signup-pro(\/|$)/,
  /\/password-recovery(\/|$)/,
  /\/password-reset(\/|$)/,
  /\/mission-response(\/|$)/,
  /\/mission-response-sms(\/|$)/,
  /\/verifyToken(\/|$)/,
];

function ApiCall() {
  const dispatch = useAppDispatch();
  const selectedLanguage = useAppSelector(state => state.language.selectedLanguage);
  const pathname = usePathname();
  const isContentRoute = !NON_CONTENT_PATH_PATTERNS.some(re => re.test(pathname || ""));

  // language-dependent payloads: fire once per language. Tracking via ref
  // (instead of state) avoids re-render loops, and the dep array no longer
  // includes derived data like `tourCategories.length` that would re-trigger
  // the effect every time a fetch lands.
  const lastLangRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isContentRoute) return;
    const language = selectedLanguage || 'en_US';
    if (lastLangRef.current === language) return;
    lastLangRef.current = language;
    dispatch(getTourCategories(true, language));
    dispatch(getFeaturedCities(true, '', true, language));
    dispatch(getRandomReviews(5, language));
  }, [dispatch, selectedLanguage, isContentRoute]);

  // Language-agnostic — fire once on mount. Social networks feed the footer,
  // so we keep this on every route (it's small ~1 KB).
  const socialNetworksFiredRef = useRef(false);
  useEffect(() => {
    if (socialNetworksFiredRef.current) return;
    socialNetworksFiredRef.current = true;
    dispatch(getSocialNetworks("true"));
  }, [dispatch]);

  return <></>;
}

export default ApiCall;
